<template>
    <modal :data-parent="($parent.$options.name === 'space-modal') ? '#map' : 'body'">
        <div slot="header">
            <button type="button" class="close" @click.prevent="closeModal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
        </div>
        <div slot="body">
            <div class="intro">
                <div class="icon">
                    <i class="icons8-message"/>
                </div>
                <h4>Ask a question</h4>
                <p v-if="!isMicrosite">
                    Many of the questions we receive about this space are answered in our
                    <a href="http://support.yourparkingspace.co.uk/" target="_blank">Support Centre</a>.
                    Please check out our FAQ’s before submitting this form.
                </p>
            </div>
            <booking-alerts :messages="states.errors" v-if="states.errors.length"></booking-alerts>
            <form @submit.prevent="submitForm">
                <div class="form-column">
                    <div class="form-group two-wide">
                        <div class="form-item">
                            <input
                                type="text"
                                class="form-control v2-styles"
                                :class="{ 'has-error': states.validation_errors['first_name'] }"
                                placeholder="First name"
                                :disabled="states.submitting"
                                v-model="formData.first_name"
                            >
                        </div>
                        <div class="form-item">
                            <input
                                type="text"
                                class="form-control v2-styles"
                                :class="{ 'has-error': states.validation_errors['first_name'] }"
                                placeholder="Last name"
                                :disabled="states.submitting"
                                v-model="formData.last_name"
                            >
                        </div>
                    </div>
                    <div class="form-group two-wide">
                        <div class="form-item">
                            <input
                                type="email"
                                class="form-control v2-styles"
                                :class="{ 'has-error': states.validation_errors['email'] }"
                                placeholder="Email address"
                                :disabled="states.submitting"
                                v-model="formData.email"
                            >
                        </div>
                        <div class="form-item">
                            <the-mask
                                type="tel"
                                class="form-control v2-styles"
                                :class="{ 'has-error': states.validation_errors['mobile'] }"
                                placeholder="Phone number"
                                :disabled="states.submitting"
                                v-model="formData.mobile"
                                :mask="['P####################']"
                                :tokens="maskTokens"
                            />
                        </div>
                    </div>
                    <div class="form-group" v-if="longTerm && shortTerm">
                        <select
                            class="form-control v2-styles dropdown-arrow"
                            :class="{ 'has-error': states.validation_errors['type'] }"
                            :disabled="states.submitting"
                            v-model="formData.type"
                        >
                            <option v-for="(type, key) in enquiryTypes" :value="key">{{ type }}</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <textarea
                            cols="30"
                            rows="10"
                            class="form-control v2-styles"
                            :class="{ 'has-error': states.validation_errors['message'] }"
                            placeholder="Message"
                            :disabled="states.submitting"
                            v-model="formData.message"
                        ></textarea>
                    </div>
                    <button type="submit" class="btn btn-green btn-block" :disabled="states.submitting">
                        <template v-if="states.submitting"><i class="fa fa-circle-o-notch fa-spin"/> Submitting&hellip;</template>
                        <template v-else>Submit</template>
                    </button>
                </div>
            </form>
        </div>
    </modal>
</template>

<script>
    import Modal from '../Modal';
    import SpaceRepository from '../../repositories/SpaceRepository';
    import cloneDeep from 'lodash/cloneDeep';
    import pickBy from 'lodash/pickBy';
    import identity from 'lodash/identity';
    import get from 'lodash/get';
    import { validationMessages } from '../../modules/formatters';
    import ModalBase from '../ModalBase.vue';
    import BookingAlerts from './booking-form/Alerts.vue';
    import Microsite from '../mixins/Microsite.vue';
    import moment from '../../modules/moment';
    import { TheMask } from 'vue-the-mask';

    export default {
        name: 'space-enquiry-modal',

        props: ['space'],

        components: {
            BookingAlerts,
            Modal,
            TheMask,
        },

        mixins: [ModalBase, Microsite],

        computed: {
            longTerm() {
                return this.space.rental_types.long
            },

            shortTerm() {
                return this.space.rental_types.short
            },

            isSearch() {
                return !this.$store.state.booking.space.id;
            },

            /**
             * Gets required data from various sources depending on where the modal is shown
             * and returns a standardised object
             *
             * @returns {Object}
             */
            searchData() {
                return [
                        'start_date',
                        'start_time',
                        'end_date',
                        'end_time',
                        'rental',
                    ]
                    .reduce((result, fieldName) => {
                        if (this.isSearch) {
                            result[fieldName] = this.$store.state.search.currentSearch[fieldName];
                            return result;
                        }
                        result[fieldName] = get(
                            this.$store.state.booking.reservation_details,
                            fieldName,
                            get(this.$store.state.booking.reservation_details.bookings[0], fieldName)
                        );
                        return result;
                    }, {});
            },

            startDateTime() {
                // Get the date
                let startDate = this.searchData.start_date;
                let format = 'DD-MM-YYYY';
                // Add the time
                startDate += ' ' + this.searchData.start_time + ':00';
                format += ' HH:mm:ss';

                return moment(startDate, format)
                    .format('YYYY-MM-DD HH:mm:ss');
            },

            endDateTime() {
                // Get the date
                let endDate = this.searchData.end_date;
                let format = 'DD-MM-YYYY';
                // Add the time
                endDate += ' ' + this.searchData.end_time + ':00';
                format += ' HH:mm:ss';

                return moment(endDate, format)
                    .format('YYYY-MM-DD HH:mm:ss');
            },

            isShortTerm() {
                return this.searchData.rental === 'short';
            },
        },

        data() {
            return {
                maskTokens: {
                    'P': {pattern: /[0|+]/},
                    '#': {pattern: /\d/},
                },
                formData: {
                    spaceId:    null,
                    first_name: null,
                    last_name:  null,
                    email:      null,
                    mobile:     null,
                    type:       null,
                    message:    null,
                    start_date: null,
                    end_date:   null,
                },
                enquiryTypes: {
                    longterm:  'Monthly Booking Enquiry',
                    shortterm: 'Daily/Hourly Booking Enquiry',
                },
                states: {
                    submitting: false,
                    errors:     [],
                    validation_errors: [],
                },
            }
        },

        mounted() {
            this.__formData = cloneDeep(this.formData);
            this.__states = cloneDeep(this.states);
            window.bus
                .$off('showSpaceContactModal')
                .$on('showSpaceContactModal', (data) => {
                    this.resetForm();
                    this.formData.spaceId = data;
                    this.showModal();
                });
        },

        methods: {
            resetForm() {
                this.formData = cloneDeep(this.__formData);
                this.resetStates();
                if (!this.isShortTerm) {
                    this.formData.type = 'longterm';
                } else {
                    this.formData.type = 'shortterm';
                }
            },

            resetStates() {
                this.states.validation_errors = [];
                this.states = cloneDeep(this.__states);
            },

            submitForm() {
                this.resetStates();
                this.states.submitting = true;
                this.formData.start_date = null;
                this.formData.end_date = null;

                // Add the dates
                this.formData.start_date = this.startDateTime;
                // Only need end dates for short term
                if (this.formData.type === 'shortterm') {
                    this.formData.end_date = this.endDateTime;
                }

                // Remove null values
                SpaceRepository.submitEnquiry(pickBy(this.formData, identity))
                    .then(() => {
                        this.resetStates();
                        this.showSuccessModal();
                    })
                    .catch((error) => {
                        this.resetStates();
                        if (error.data.status_code === 422) {
                            this.states.validation_errors = error.data.errors;
                        }
                        this.states.errors = validationMessages(error.data.errors);
                    });
            },

            showSuccessModal() {
                this.hideModal();
                this.$once('hidden', () => {
                    bus.$emit('showGenericModal', {
                        iconClass: 'icons8-message',
                        content: `
                            <h4>Thank you for your message</h4>
                            <p>We will be in touch regarding your query as soon as possible. In the meantime, many of the questions we receive are answered in our <a href="https://support.yourparkingspace.co.uk" target="_blank">FAQ section</a>.</p>
                        `,
                        closeButtonText: 'Done',
                    });
                });
            },

            closeModal() {
                $('#space-enquiry-modal').modal('hide');
            },
        },
    }
</script>

<style lang="less">
    @import (reference) "~assets/less/variables.less";

    #space-enquiry-modal {
        &[data-parent='#map'] {
            @media (min-width: @screen-md-min) {
                position: absolute;
            }
        }
        .modal-dialog {
            @media (min-width: @screen-sm-min) {
                width: 700px;
            }
        }
    }
</style>

<style lang="less" scoped>
    @import (reference) "~assets/less/variables.less";
    @import (reference) "~bootstrap/less/mixins/grid.less";
    @import (reference) "~bootstrap/less/mixins/clearfix.less";

    .booking-form-alert {
        margin-bottom: 30px;
    }

    .intro {
        text-align: center;
        font-size: 16px;
        margin-bottom: 30px;
        a {
            color: @YPS-secondary;
            font-weight: 600;
        }
        .icon {
            width: 91px;
            height: 91px;
            display: table;
            background-color: #ebf2e1;
            border-radius: 50%;
            line-height: 91px;
            color: @YPS-secondary;
            font-size: 50px;
            margin: 0 auto 23px;

            i {
                display: table-cell;
                width: 100%;
                height: 100%;
                vertical-align: middle;
            }
        }
        h4 {
            color: #40424b;

            font-family: @headings-font-family;
            font-size: 23px;
            font-weight: 700;
            margin-bottom: 20px;
        }
    }
    form {
        text-align: center;
        .make-row(10px);
        .clearfix;
        .form-column {
            .make-sm-column(12, 10px);
        }
        .form-group {
            margin-bottom: 12px;

            &.two-wide {
                margin-bottom: 0;
                .make-row(10px);
                .clearfix();

                .form-item {
                    .make-sm-column(6, 10px);
                    margin-bottom: 12px;
                }
            }
        }
        textarea.form-control {
            height: 92px;
        }
        button.btn {
            color: #ffffff;
            font-family: @headings-font-family;
            font-size: 13px;
            font-weight: 700;
            border-radius: 2px;
            padding: 10px 45px;
            display: inline-block;
            width: auto;
        }
    }
</style>
